import { Box, Button, TextField, Typography } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { useIntl } from "react-intl";
import Messages from "../../../../localization/Messages";
import { DateFormat, getDateMoment, getDateNumber } from "../../../../helpers/dateHelper";
import {
    getStatsExpensePerDock,
    getStatsExpensePerShip,
    getStatsExpensePerShipAndDock,
} from "../../../../api/stats";
import {
    getFileName,
    getStatsExpensePerDockValues,
    getStatsExpensePerShipAndDockValues,
    getStatsExpensePerShipValues,
} from "../../../StatisticsPage/shared/utils";
import { CsvStatisticsFilters } from "../../../StatisticsPage/shared/filters";

import { showBackendMessage } from "../../../../helpers/messagesHelper";
import {
    statsExpensePerShipColumnDefs,
    statsPerDockColumnDefs,
    statsPerShipAndDockColumnDefs,
} from "../../../StatisticsPage/shared/constants";
import { useDispatch } from "react-redux";
import { IDock } from "../../../../models/dock";
import { IAddress } from "../../../../models/address";
import { useGuiConfState } from "../../../../hooks/useGuiConfState";
import { useCustomExportToCsv } from "../../../../hooks/useCustomExportToCsv";

type ExportStatisticsContainerProps = {
    docks: IDock[];
    managingCompanies: Map<any, any>;
    owningCompanies: IAddress[];
};

export const ExportStatisticsContainer = ({
    docks,
    managingCompanies,
    owningCompanies,
}: ExportStatisticsContainerProps) => {
    const exportStats = useCustomExportToCsv();
    const intl = useIntl();
    const dispatch = useDispatch();
    const [csvFilters, setCsvFilters] = useGuiConfState("dispCsvStatistics");
    const handleChange = (diff: Partial<CsvStatisticsFilters>) => {
        setCsvFilters(diff);
    };

    const onExportShipStatistics = async (csvFilters: CsvStatisticsFilters) => {
        try {
            const res = await getStatsExpensePerShip(csvFilters);
            const dockNames = docks.map(dock => dock.name);
            const values = res.data.data.map(data =>
                getStatsExpensePerShipValues(
                    data,
                    csvFilters,
                    managingCompanies,
                    owningCompanies,
                    docks,
                ),
            );
            const fileName = getFileName(
                intl.formatMessage(Messages.expensePerShipStatistics),
                csvFilters.from,
                csvFilters.to,
            );
            exportStats(statsExpensePerShipColumnDefs, values, dockNames, fileName);
        } catch {
            dispatch(showBackendMessage(intl, "error", "fetching", Messages.shipStatistics));
        }
    };

    const onExportDockStatistics = async (csvFilters: CsvStatisticsFilters) => {
        try {
            const res = await getStatsExpensePerDock(csvFilters);
            const values = res.data.data.map(data =>
                getStatsExpensePerDockValues(data, csvFilters, docks),
            );
            const fileName = getFileName(
                intl.formatMessage(Messages.expensePerDockStatistics),
                csvFilters.from,
                csvFilters.to,
            );
            exportStats(statsPerDockColumnDefs, values, undefined, fileName);
        } catch {
            dispatch(showBackendMessage(intl, "error", "fetching", Messages.docskStatistics));
        }
    };

    const onExportShipAndDockStatistics = async (csvFilters: CsvStatisticsFilters) => {
        try {
            const res = await getStatsExpensePerShipAndDock(csvFilters);
            const values = res.data.data.map(data =>
                getStatsExpensePerShipAndDockValues(data, csvFilters),
            );
            const fileName = getFileName(
                intl.formatMessage(Messages.expensePerShipAndDockStatistics),
                csvFilters.from,
                csvFilters.to,
            );
            exportStats(statsPerShipAndDockColumnDefs, values, undefined, fileName);
        } catch {
            dispatch(showBackendMessage(intl, "error", "fetching", Messages.shipAndDockStatistics));
        }
    };

    return (
        <Box className="export-statistics-container shadow" gap={3}>
            <Typography variant="caption" fontWeight={"bold"} fontSize={"20px"}>
                {intl.formatMessage(Messages.exportStatisticLists)}
            </Typography>
            <Box display="flex " gap={2} className="export-csvFilters">
                <DesktopDatePicker
                    label={intl.formatMessage(Messages.from)}
                    inputFormat={DateFormat.CLIENT_DATE}
                    value={getDateMoment(csvFilters.from)}
                    onChange={value =>
                        (!isNaN(getDateNumber(value)) || value === null) &&
                        handleChange({ from: getDateMoment(value).valueOf() })
                    }
                    renderInput={params => <TextField variant="filled" {...params} />}
                />
                <DesktopDatePicker
                    label={intl.formatMessage(Messages.to)}
                    inputFormat={DateFormat.CLIENT_DATE}
                    value={getDateMoment(csvFilters.to)}
                    onChange={value =>
                        (!isNaN(getDateNumber(value)) || value === null) &&
                        handleChange({ to: getDateMoment(value).valueOf() })
                    }
                    renderInput={params => <TextField variant="filled" {...params} />}
                />
            </Box>

            <Box display="flex" gap={10} className="export-buttons">
                <Button variant="contained" onClick={() => onExportShipStatistics(csvFilters)}>
                    {intl.formatMessage(Messages.exportExpensePerShipStatistics)}
                </Button>
                <Button variant="contained" onClick={() => onExportDockStatistics(csvFilters)}>
                    {intl.formatMessage(Messages.exportExpensePerDockStatistics)}
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => onExportShipAndDockStatistics(csvFilters)}
                >
                    {intl.formatMessage(Messages.exportExpensePerShipAndDockStatistics)}
                </Button>
            </Box>
        </Box>
    );
};
